'use client';

/**
 * Shared Styled Components for Guidance System
 *
 * Common styled components used across CommandPalette, FeatureHint, HelpPanel, and SpotlightTour.
 * Centralizes keyboard hints, search inputs, glass containers, and other reusable UI patterns.
 */

import type { BoxProps, InputBaseProps } from '@mui/material';
import { alpha, Box, InputBase } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { glassEffects } from '../../theme/shadows';

// Type alias for styled components - avoids direct reference to internal MUI types
type StyledBox<P = object> = React.ComponentType<BoxProps & P>;
type StyledInputBase<P = object> = React.ComponentType<InputBaseProps & P>;
type StyledKbd = React.ComponentType<React.HTMLAttributes<HTMLElement>>;

// ============================================================================
// Glass Container
// ============================================================================

export interface GlassContainerProps {
  $isDark: boolean;
  $maxWidth?: number;
  $padding?: number;
  $borderRadius?: number;
}

export const GlassContainer: StyledBox<GlassContainerProps> = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<GlassContainerProps>(({ theme, $isDark, $maxWidth = 300, $padding = 1.5, $borderRadius = 12 }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    padding: theme.spacing($padding),
    borderRadius: $borderRadius,
    maxWidth: $maxWidth,
    background: $isDark ? 'rgba(13, 33, 55, 0.98)' : 'rgba(255, 255, 255, 0.98)',
    backdropFilter: glass.backdropBlur,
    border: `1px solid ${glass.border}`,
    boxShadow: `0 8px 32px -4px rgba(0, 0, 0, 0.2), 0 0 0 1px ${alpha(theme.palette.primary.main, 0.05)}`,
  };
}) as StyledBox<GlassContainerProps>;

// ============================================================================
// Search Input Container
// ============================================================================

export const SearchInputContainer: StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0.75, 1.5),
  borderRadius: 8,
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.05) : alpha(theme.palette.common.black, 0.04),
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 150ms ease',

  '&:focus-within': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
})) as StyledBox;

export const SearchInput: StyledInputBase = styled(InputBase)(({ theme }) => ({
  flex: 1,
  marginLeft: theme.spacing(1),
  fontSize: '0.875rem',

  '& .MuiInputBase-input': {
    padding: theme.spacing(0.5, 0),
    '&::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 0.7,
    },
  },
})) as StyledInputBase;

// ============================================================================
// Keyboard Key
// ============================================================================

export const KeyboardKey: StyledKbd = styled('kbd')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 20,
  height: 20,
  padding: '0 5px',
  fontSize: '0.6875rem',
  fontFamily: 'inherit',
  fontWeight: 600,
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.08) : alpha(theme.palette.common.black, 0.06),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 4,
  boxShadow: `0 1px 0 ${theme.palette.divider}`,
})) as StyledKbd;

// ============================================================================
// Keyboard Hint
// ============================================================================

export const KeyboardHintContainer: StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  color: theme.palette.text.secondary,
  fontSize: '0.75rem',
})) as StyledBox;

export interface KeyboardHintProps {
  keys: string[];
  label?: string;
}

export const KeyboardHint: React.FC<KeyboardHintProps> = ({ keys, label }) => (
  <KeyboardHintContainer>
    {keys.map((key, i) => (
      <React.Fragment key={i}>
        <KeyboardKey>{key}</KeyboardKey>
        {i < keys.length - 1 && <span>+</span>}
      </React.Fragment>
    ))}
    {label && <span style={{ marginLeft: 4 }}>{label}</span>}
  </KeyboardHintContainer>
);

// ============================================================================
// Section Header
// ============================================================================

export const SectionHeader: StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.02) : alpha(theme.palette.common.black, 0.02),
})) as StyledBox;

// ============================================================================
// List Item Button
// ============================================================================

export interface GuidanceListItemButtonProps {
  $isActive?: boolean;
}

export const GuidanceListItemButton: StyledBox<GuidanceListItemButtonProps> = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<GuidanceListItemButtonProps>(({ theme, $isActive }) => ({
  display: 'flex',
  alignItems: 'center',
  margin: theme.spacing(0.5, 1),
  borderRadius: 8,
  padding: theme.spacing(1, 1.5),
  cursor: 'pointer',
  backgroundColor: $isActive
    ? theme.palette.mode === 'dark'
      ? alpha(theme.palette.primary.main, 0.15)
      : alpha(theme.palette.primary.main, 0.1)
    : 'transparent',
  border: $isActive ? `1px solid ${alpha(theme.palette.primary.main, 0.3)}` : '1px solid transparent',
  transition: 'all 100ms ease',

  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.12) : alpha(theme.palette.primary.main, 0.08),
  },
})) as StyledBox<GuidanceListItemButtonProps>;

// ============================================================================
// Shortcut Chip
// ============================================================================

export const ShortcutChip: StyledBox = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  height: 22,
  padding: '0 6px',
  fontSize: '0.75rem',
  fontFamily: 'monospace',
  fontWeight: 600,
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.08) : alpha(theme.palette.common.black, 0.06),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 4,
})) as StyledBox;

// ============================================================================
// Panel Footer
// ============================================================================

export const PanelFooter: StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 2),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.02) : alpha(theme.palette.common.black, 0.02),
})) as StyledBox;

// ============================================================================
// Spotlight Overlay
// ============================================================================

export interface SpotlightOverlayProps {
  $isDark: boolean;
}

export const SpotlightOverlay: StyledBox<SpotlightOverlayProps> = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<SpotlightOverlayProps>(({ $isDark }) => ({
  position: 'fixed',
  inset: 0,
  backgroundColor: $isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)',
  backdropFilter: 'blur(2px)',
  zIndex: 1300,
})) as StyledBox<SpotlightOverlayProps>;
